import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip,
  Card,
  CardContent,
  Tab,
  Tabs,
  Badge,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MaterialTable from "@material-table/core";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import dayjs, { Dayjs } from "dayjs";

import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LayersIcon from "@mui/icons-material/Layers";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DoorFrontIcon from "@mui/icons-material/DoorFront";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import PaymentsIcon from "@mui/icons-material/Payments";

import DateRangePicker from "../../../../components/inputs/daterangepickerv2";
import * as StoreApi from "../../../../api/storeApi";
import { UserEventType } from "../../../../constants";
import { splitCamelCase } from "../../../../utils";
import { UserOverview } from "../../../users/UserOverview";
import CartActions from "../../../users/collections/CartAction";
import UserPaymentEvent from "../../../users/collections/UserPaymentEvent";
import { UserContext } from "../../../../UserProvider";
import { useI18n } from "../../../../hooks/useI18n";
import title from "material-ui/svg-icons/editor/title";

const useCustomStyles = () => {
  const theme = useTheme();
  const user = useContext(UserContext);
  const primaryColor =
    user.tenant.configuration?.primaryColor || theme.palette.primary.main;

  return {
    root: {
      padding: theme.spacing(3),
      backgroundColor: "#f5f7fa",
      minHeight: "100vh"
    },
    header: {
      marginBottom: theme.spacing(3)
    },
    datePickerWrapper: {
      marginRight: theme.spacing(2)
    },
    card: {
      marginBottom: theme.spacing(3),
      borderRadius: theme.spacing(1),
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      border: "1px solid #e0e0e0"
    },
    filterBadge: {
      "& .MuiBadge-badge": {
        backgroundColor: primaryColor,
        color: "white"
      }
    },
    filterChip: {
      margin: theme.spacing(0.5),
      backgroundColor: `${primaryColor}20`,
      color: primaryColor
    },
    tableContainer: {
      "& .MuiPaper-root": {
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1)
      }
    },
    statusSuccess: {
      color: theme.palette.success.main
    },
    statusFailed: {
      color: theme.palette.error.main,
      opacity: 0.7
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    },
    eventDetailTableContainer: {
      backgroundColor: "#f9f9f9",
      borderRadius: theme.spacing(1)
    },
    insightCard: {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between"
    },
    insightValue: {
      fontWeight: "bold",
      fontSize: "1.8rem",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1)
    },
    tabs: {
      marginBottom: theme.spacing(3),
      backgroundColor: "white",
      borderRadius: theme.spacing(1),
      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
      border: "1px solid #e0e0e0"
    }
  };
};

const fetchStoreEvents = async (storeId, startDate, endDate) => {
  const result = await StoreApi.listEvents(storeId, startDate, endDate);
  return result;
};

export const StoreEvents = () => {
  const theme = useTheme();
  const styles = useCustomStyles();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useI18n();

  const user = useContext(UserContext);
  const primaryColor =
    user.tenant.configuration?.primaryColor || theme.palette.primary.main;

  const { stores } = useSelector(state => state.stores);
  const store = stores[0];

  const [userId, setUserId] = useState("");
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterOpenNoPay, setFilterOpenNoPay] = useState(false);

  const [filterAnyFailedPayment, setFilterAnyFailedPayment] = useState(false);
  const [filterEmptiedCart, setFilterEmptiedCart] = useState(false);

  const [filterRemovesItemsFromCart, setFilterRemovesItemsFromCart] =
    useState(false);

  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs());
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());

  const formattedStartDate = startDate
    ? startDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;
  const formattedEndDate = endDate
    ? endDate.format("YYYY-MM-DDTHH:mm:ssZ")
    : null;

  const [activeTab, setActiveTab] = useState(0);

  const handleDateRangeChange = ([start, end]) => {
    setStartDate(start);
    setEndDate(end);
  };

  const {
    data: events = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ["storeEvents", store?.id, formattedStartDate, formattedEndDate],
    () => fetchStoreEvents(store?.id, formattedStartDate, formattedEndDate),
    {
      enabled: !!store,
      staleTime: Infinity
    }
  );

  const getUserPatterns = events => {
    const userPatterns = {};

    events.forEach(event => {
      const userId = event.user?.id;
      if (!userId) return;

      if (!userPatterns[userId]) {
        userPatterns[userId] = {
          openedDoor: false,
          madePayment: false,
          failedPayments: 0,
          emptiedCart: false,
          sessionIds: new Set(),
          events: []
        };
      }

      userPatterns[userId].events.push(event);
      userPatterns[userId].sessionIds.add(event.sessionId);

      if (event.event === UserEventType.OPEN_DOOR && event.successful) {
        userPatterns[userId].openedDoor = true;
      }

      if (event.event === UserEventType.PAYMENT) {
        if (event.successful) {
          userPatterns[userId].madePayment = true;
        } else {
          userPatterns[userId].failedPayments += 1;
        }
      }

      if (event.event === UserEventType.CART_EMPTY) {
        userPatterns[userId].emptiedCart = true;
      }
    });

    return userPatterns;
  };

  const groupEvents = events => {
    const groupedEvents = [];
    const sessionGroups = {};

    events.forEach(event => {
      if (!sessionGroups[event.sessionId]) {
        sessionGroups[event.sessionId] = [];
      }
      sessionGroups[event.sessionId].push(event);
    });

    Object.values(sessionGroups).forEach(sessionEvents => {
      let groupedRow = null;

      sessionEvents.forEach(event => {
        if (
          event.event === UserEventType.SCANNER_ON_SCAN_FOUND ||
          event.event === UserEventType.CART_INCREASE ||
          event.event === UserEventType.CART_DECREASE ||
          event.event === UserEventType.SCANNER_START_SCANNING ||
          event.event === UserEventType.CART_EMPTY
        ) {
          if (!groupedRow) {
            groupedRow = {
              ...event,
              event: "GROUPED_SCAN_ACTIONS",
              dynamicData: {
                ...event.dynamicData,
                groupedActions: [event]
              }
            };
          } else {
            groupedRow.dynamicData.groupedActions?.push(event);
          }
        } else {
          if (groupedRow) {
            groupedEvents.push(groupedRow);
            groupedRow = null;
          }
          groupedEvents.push(event);
        }
      });

      if (groupedRow) {
        groupedEvents.push(groupedRow);
      }
    });

    return groupedEvents;
  };

  const filterEvents = events => {
    if (
      !filterOpenNoPay &&
      !filterAnyFailedPayment &&
      !filterEmptiedCart &&
      !filterRemovesItemsFromCart
    ) {
      return events;
    }

    const groupedEvents = groupEvents(events);

    return groupedEvents.filter(event => {
      if (
        filterAnyFailedPayment &&
        event.event === UserEventType.PAYMENT &&
        !event.successful
      ) {
        return true;
      }

      if (
        filterEmptiedCart &&
        event.event === "GROUPED_SCAN_ACTIONS" &&
        event.dynamicData?.groupedActions?.some(
          action => action.event === UserEventType.CART_EMPTY
        )
      ) {
        return true;
      }

      if (filterOpenNoPay) {
        const userPatterns = getUserPatterns(events);
        const userId = event.user?.id;

        if (
          userId &&
          userPatterns[userId]?.openedDoor &&
          !userPatterns[userId]?.madePayment &&
          event.event === UserEventType.OPEN_DOOR &&
          event.successful
        ) {
          return true;
        }
      }

      if (
        filterRemovesItemsFromCart &&
        event.event === "GROUPED_SCAN_ACTIONS" &&
        event.dynamicData?.groupedActions?.some(
          action => action.event === UserEventType.CART_DECREASE
        )
      ) {
        return true;
      }

      return false;
    });
  };

  const renderEvent = row => {
    let eventIcon;
    let eventLabel = row.event;
    let eventColor = theme.palette.text.primary;

    if (row.event === UserEventType.OPEN_DOOR) {
      eventIcon = (
        <DoorFrontIcon style={{ opacity: !row.successful ? 0.5 : 1 }} />
      );
      eventLabel = t("common.tabs.doorAccess");
    } else if (row.event === UserEventType.PAYMENT) {
      eventIcon = (
        <PaymentsIcon
          style={{
            color: row.successful
              ? theme.palette.success.main
              : theme.palette.text.disabled
          }}
        />
      );
      eventLabel = t("common.tabs.payment");
      eventColor = row.successful
        ? theme.palette.success.main
        : theme.palette.error.main;
    } else if (row.event === "GROUPED_SCAN_ACTIONS") {
      eventIcon = <ShoppingCartIcon style={{ color: primaryColor }} />;
      eventLabel = t("common.tabs.cartUpdates");
      eventColor = primaryColor;
    }

    return (
      <Box display="flex" alignItems="center" gap={1}>
        {eventIcon}
        {!mobile && (
          <Typography variant="body2" color={eventColor}>
            {eventLabel}
          </Typography>
        )}
        {!row.successful && (
          <Chip
            size="small"
            label={t("common.status.failed")}
            sx={{
              backgroundColor: "#fdeded",
              color: theme.palette.error.main,
              height: "20px"
            }}
          />
        )}
      </Box>
    );
  };

  const renderEventDetailPanel = rowData => {
    const hasFailureReason = Boolean(rowData.dynamicData?.failureReason);

    return {
      icon: () => {
        if (
          hasFailureReason ||
          rowData.event === UserEventType.PAYMENT ||
          rowData.event === "GROUPED_SCAN_ACTIONS"
        ) {
          return <ChevronRightIcon fontSize="small" />;
        }
        return <ChevronRightIcon style={{ fontSize: 0 }} />;
      },
      render: () => {
        return (
          <Box
            sx={{
              padding: theme.spacing(2),
              backgroundColor: "#fafafa",
              borderRadius: "0 0 8px 8px",
              borderTop: `1px solid ${theme.palette.divider}`
            }}
          >
            <Box width="100%">
              {rowData.dynamicData?.paymentMethod && (
                <Box mb={1}>
                  <Chip
                    label={splitCamelCase(rowData.dynamicData.paymentMethod)}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              )}

              {hasFailureReason ? (
                <Box
                  sx={{
                    padding: theme.spacing(1.5),
                    backgroundColor: "#fdeded",
                    borderRadius: theme.spacing(1)
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <WarningAmberIcon color="error" fontSize="small" />
                    <Typography variant="body2" color="error">
                      {splitCamelCase(rowData.dynamicData.failureReason)}
                    </Typography>
                  </Box>
                </Box>
              ) : rowData.successful ? (
                rowData.event === "GROUPED_SCAN_ACTIONS" ? (
                  <Box>
                    <Box
                      justifyContent="center"
                      flexDirection="column"
                      display="flex"
                    >
                      <CartActions
                        actions={rowData.dynamicData.groupedActions}
                      />
                    </Box>
                  </Box>
                ) : (
                  <UserPaymentEvent
                    userId={rowData.user.id}
                    chargeId={rowData.dynamicData.chargeId}
                  />
                )
              ) : null}
            </Box>
          </Box>
        );
      }
    };
  };

  const renderFilterDialog = () => (
    <Dialog
      open={filterDialogOpen}
      onClose={() => setFilterDialogOpen(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(1),
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <FilterListIcon style={{ marginRight: 8, color: primaryColor }} />
          <Typography variant="h6">
            {t("common.messages.filterEvents")}
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          sx={styles.closeButton}
          onClick={() => setFilterDialogOpen(false)}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                checked={filterOpenNoPay}
                onChange={() => setFilterOpenNoPay(!filterOpenNoPay)}
                name="filterOpenNoPay"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.openedDoorWithoutPurchase")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterAnyFailedPayment}
                onChange={() =>
                  setFilterAnyFailedPayment(!filterAnyFailedPayment)
                }
                name="filterAnyFailedPayment"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.paymentFailure")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterEmptiedCart}
                onChange={() => setFilterEmptiedCart(!filterEmptiedCart)}
                name="filterEmptiedCart"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.abandonedCart")}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterRemovesItemsFromCart}
                onChange={() =>
                  setFilterRemovesItemsFromCart(!filterRemovesItemsFromCart)
                }
                name="removesItemsFromCart"
                color="primary"
                sx={{
                  color: primaryColor,
                  "&.Mui-checked": {
                    color: primaryColor
                  }
                }}
              />
            }
            label={t("common.messages.removesItemsFromCart")}
          />
        </FormGroup>
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            color="inherit"
            onClick={() => {
              setFilterOpenNoPay(false);
              setFilterAnyFailedPayment(false);
              setFilterEmptiedCart(false);
              setFilterRemovesItemsFromCart(false);
            }}
            style={{ marginRight: 8 }}
          >
            {t("common.actions.clearFilters")}
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: primaryColor }}
            onClick={() => setFilterDialogOpen(false)}
          >
            {t("common.actions.apply")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );

  const renderUserDialog = () => (
    <Dialog
      onClose={() => setUserId("")}
      open={userId !== ""}
      fullWidth={true}
      fullScreen={mobile}
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: mobile ? 0 : theme.spacing(1),
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle sx={{ height: 50, padding: theme.spacing(1) }}>
        <IconButton
          aria-label="close"
          sx={styles.closeButton}
          onClick={() => setUserId("")}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={mobile ? { padding: 0 } : {}}>
        <UserOverview inputUserId={userId} />
      </DialogContent>
    </Dialog>
  );

  const renderFilterBar = () => (
    <Card sx={styles.card}>
      <CardContent>
        <Box
          display="flex"
          flexDirection={mobile ? "column" : "row"}
          alignItems={mobile ? "flex-start" : "center"}
          mb={mobile ? 2 : 0}
        >
          <Box display="flex" alignItems="center" mb={mobile ? 2 : 0}>
            <LayersIcon sx={{ marginRight: 1, color: primaryColor }} />
            <Typography
              variant="subtitle2"
              component="span"
              fontWeight={500}
              marginRight={2}
            >
              {t("common.salesAndVisitors")}
            </Typography>
          </Box>

          <Box
            display="flex"
            flexDirection={mobile ? "column" : "row"}
            alignItems={mobile ? "flex-start" : "center"}
            width="100%"
          >
            <Box sx={styles.datePickerWrapper} mb={mobile ? 2 : 0}>
              <DateRangePicker
                onDateRangeChange={handleDateRangeChange}
                maxDate={dayjs()}
              />
            </Box>

            <Box ml={mobile ? 0 : "auto"} display="flex" gap={1}>
              <Badge
                badgeContent={
                  filterOpenNoPay ||
                  filterAnyFailedPayment ||
                  filterEmptiedCart ||
                  filterRemovesItemsFromCart
                    ? "!"
                    : 0
                }
                sx={styles.filterBadge}
              >
                <Button
                  startIcon={<FilterListIcon />}
                  onClick={() => setFilterDialogOpen(true)}
                  sx={{
                    ...(filterOpenNoPay ||
                    filterAnyFailedPayment ||
                    filterEmptiedCart
                      ? {
                          backgroundColor: primaryColor,
                          color: "white",
                          "&:hover": {
                            backgroundColor: primaryColor
                          }
                        }
                      : {
                          color: primaryColor,
                          borderColor: primaryColor,
                          "&:hover": {
                            borderColor: primaryColor,
                            backgroundColor: `${primaryColor}10`
                          }
                        })
                  }}
                  variant={
                    filterOpenNoPay ||
                    filterAnyFailedPayment ||
                    filterEmptiedCart
                      ? "contained"
                      : "outlined"
                  }
                  size="small"
                >
                  {t("common.actions.filter")}
                </Button>
              </Badge>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderAppliedFilters = () => {
    if (
      !filterOpenNoPay &&
      !filterAnyFailedPayment &&
      !filterEmptiedCart &&
      !filterRemovesItemsFromCart
    )
      return null;

    return (
      <Box mb={3}>
        <Paper
          elevation={0}
          sx={{
            padding: theme.spacing(1.5),
            backgroundColor: `${primaryColor}10`,
            borderRadius: theme.spacing(1),
            display: "flex",
            alignItems: "center"
          }}
        >
          <InfoOutlinedIcon sx={{ marginRight: 1, color: primaryColor }} />
          <Typography variant="body2" sx={{ flex: 1 }}>
            {t("common.actions.filterApplied")}
          </Typography>

          <Box>
            {filterOpenNoPay && (
              <Chip
                label={t("common.messages.doorOpenedWithoutPayment")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterOpenNoPay(false)}
              />
            )}

            {filterAnyFailedPayment && (
              <Chip
                label={t("common.messages.paymentFailure")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterAnyFailedPayment(false)}
              />
            )}

            {filterEmptiedCart && (
              <Chip
                label={t("common.messages.abandonedCart")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterEmptiedCart(false)}
              />
            )}

            {filterRemovesItemsFromCart && (
              <Chip
                label={t("common.messages.removesItemsFromCart")}
                size="small"
                sx={styles.filterChip}
                onDelete={() => setFilterRemovesItemsFromCart(false)}
              />
            )}

            <Button
              size="small"
              onClick={() => {
                setFilterOpenNoPay(false);
                setFilterAnyFailedPayment(false);
                setFilterEmptiedCart(false);
                setFilterRemovesItemsFromCart(false);
              }}
              sx={{ marginLeft: 1, color: primaryColor }}
            >
              {t("common.actions.clearFilters")}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderTabs = () => (
    <Paper sx={styles.tabs}>
      <Tabs
        value={activeTab}
        onChange={(_, newValue) => setActiveTab(newValue)}
        variant={mobile ? "fullWidth" : "standard"}
        centered={!mobile}
        sx={{
          "& .MuiTabs-indicator": {
            backgroundColor: primaryColor
          },
          "& .Mui-selected": {
            color: primaryColor
          }
        }}
      >
        <Tab label={t("common.tabs.allEvents")} />
        <Tab label={t("common.tabs.doorAccess")} />
        <Tab label={t("common.tabs.payments")} />
      </Tabs>
    </Paper>
  );

  const getTabFilteredEvents = events => {
    if (activeTab === 0) return events;

    if (activeTab === 1) {
      return events.filter(event => event.event === UserEventType.OPEN_DOOR);
    }

    if (activeTab === 2) {
      return events.filter(event => event.event === UserEventType.PAYMENT);
    }

    return events;
  };

  const filteredEvents = filterEvents(events);
  const tabFilteredEvents = getTabFilteredEvents(filteredEvents);
  const groupedEvents = groupEvents(tabFilteredEvents);

  if (error) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6">
          Error loading data
        </Typography>
        <Button
          variant="contained"
          sx={{ marginTop: 2, backgroundColor: primaryColor }}
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={styles.root}>
      {renderFilterBar()}

      {renderAppliedFilters()}

      {renderTabs()}

      <Box sx={styles.tableContainer}>
        <MaterialTable
          title=""
          columns={[
            {
              title: t("common.tableHeaders.date"),
              field: "timestamp",
              cellStyle: { padding: mobile ? "8px" : "16px" },
              render: rowData => (
                <Typography variant="body2">
                  {moment(rowData.timestamp).format("YYYY-MM-DD HH:mm")}
                </Typography>
              )
            },
            {
              title: t("common.tableHeaders.event"),
              field: "event",
              cellStyle: { padding: mobile ? "8px" : "16px" },
              render: row => renderEvent(row)
            },
            {
              title: t("common.tableHeaders.userId"),
              field: "user.id",
              cellStyle: { padding: mobile ? "8px" : "16px" },
              render: rowData => (
                <Tooltip title={t("common.tooltip.viewUserDetails")}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setUserId(rowData.user.id)}
                    sx={{ color: primaryColor }}
                  >
                    {`${rowData.user.firstName} ${rowData.user.lastName}`}
                  </Button>
                </Tooltip>
              )
            },
            {
              title: "Status",
              field: "successful",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.userId"),
              field: "user.id",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.firstName"),
              field: "user.firstName",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.lastName"),
              field: "user.lastName",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.receiptNr"),
              field: "dynamicData.receiptId",
              hidden: true,
              export: true
            },
            {
              title: "Products & Price",
              field: "dynamicData.receiptItems",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.paymentMethod"),
              field: "dynamicData.paymentMethod",
              hidden: true,
              export: true
            },
            {
              title: t("common.tableHeaders.failureReason"),
              field: "dynamicData.failureReason",
              hidden: true,
              export: true
            }
          ]}
          data={groupedEvents}
          isLoading={isLoading}
          detailPanel={[rowData => renderEventDetailPanel(rowData)]}
          options={{
            rowStyle: row => ({
              backgroundColor: !row.successful ? "#fff5f5" : "white",
              borderLeft: !row.successful
                ? `4px solid ${theme.palette.error.light}`
                : "none",
              cursor: "pointer"
            }),
            headerStyle: {
              backgroundColor: "#f5f7fa",
              fontWeight: 600,
              padding: mobile ? "8px" : "16px"
            },
            pageSize: 10,
            pageSizeOptions: [5, 10, 20, 50],
            emptyRowsWhenPaging: false,
            search: true,
            searchFieldAlignment: "left",
            searchFieldStyle: {
              borderRadius: theme.spacing(0.5)
            },
            padding: "dense",
            elevation: 0
          }}
        />
      </Box>

      {renderFilterDialog()}

      {renderUserDialog()}
    </Box>
  );
};
